import type React from "react";

import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Typography } from "@mui/material";
import type { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { Notification } from "shared/atoms/Notification";
import { Spinner } from "shared/atoms/Spinner";

interface Props {
  linkTo: string;
  icon: IconProp;
  label: string;
  notifications?: number;
  error?: AxiosError | unknown;
  isLoading?: boolean;
  dataTestId?: string;
}

export const EventCard: React.FC<Props> = ({
  linkTo,
  notifications = 0,
  icon,
  label,
  error = false,
  isLoading = false,
  dataTestId = "event-card",
}) => {
  const { t } = useTranslation();
  const hasNotifications = notifications > 0;

  return (
    <StyledLink to={hasNotifications ? linkTo : " "} $notifications={notifications} data-testid={dataTestId}>
      <StyledCard $notifications={notifications} $withError={!!error}>
        <IconWrapper data-testid="icon">
          <FontAwesomeIcon icon={icon} />
        </IconWrapper>
        <Typography variant="body1" flex={1}>
          {label}
        </Typography>
        {!error && isLoading && <Spinner small style={{ height: 34, width: 34 }} />}
        {!error && !isLoading && hasNotifications && <Notifications>{notifications}</Notifications>}
        {!error && !isLoading && !hasNotifications && (
          <FontAwesomeIcon data-testid="check-icon" icon={faCheckCircle} fontSize={32} color="#4FC577" />
        )}
        {!!error && <Break />}
        {!!error && <StyledNotification variant="danger">{t("errors.generic")}</StyledNotification>}
      </StyledCard>
    </StyledLink>
  );
};

const StyledCard = styled.div.withConfig({
  shouldForwardProp: prop => !["$notifications", "$withError"].includes(prop),
})<{
  $notifications: number;
  $withError: boolean;
}>`
  border: 1px solid ${({ theme }) => theme.colors.redesign.db20};
  background: ${({ theme }) => theme.semantic.container.background};
  border-radius: 12px;
  min-height: 18px;
  padding: 12px 10px;
  ${props => props.theme.aboveBreakpoint} {
    padding: 24px 20px;
  }

  display: flex;
  flex-direction: row;
  align-items: center;
  color: ${({ theme }) => theme.colors.primary.base};

  user-select: none;
  &:hover {
    background: ${props => props.theme.colors.redesign.b10};
    border: 1px solid ${({ theme }) => theme.colors.redesign.b100};
    transition: 0.2s ease-in-out;
  }
  ${({ $withError }) =>
    $withError &&
    `
      height: 120px;
    `}
`;

const StyledLink = styled(Link).withConfig({
  shouldForwardProp: prop => prop !== "$notifications",
})<{ $notifications: number }>`
  ${({ $notifications }) => $notifications === 0 && `pointer-events: none;`}
  text-decoration: none;
  color: inherit;
`;

const IconWrapper = styled.div`
  display: flex;
  svg {
    width: 18px;
    height: 18px;
    padding: 0 16px 0 0;
  }
`;

const Notifications = styled.div`
  ${({ theme }) => ({ ...theme.font.link1 })};
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.redesign.r10};
  padding: 4px 8px;
  color: ${({ theme }) => theme.colors.redesign.r100};
`;

const StyledNotification = styled(Notification)`
  max-height: 32px;
  width: 100%;
`;

const Break = styled.div`
  flex-basis: 100%;
  height: 0;
`;
